/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import type { NavCategory, NavTopCategory } from '@/lib/navigation/types';
import { SubnavGroups } from '@/components/navigation/subnav-groups';

/** The drawer eats most of the width, so the category area gets far fewer columns. */
function availableColumns(hasContentSlot: boolean): number {
    return hasContentSlot ? 2 : 3;
}

function DrawerEntry({
    category,
    isActive,
    onActivate,
    onNavigate,
}: {
    category: NavCategory;
    isActive: boolean;
    onActivate: () => void;
    onNavigate?: () => void;
}): ReactElement {
    return (
        <li>
            {/* A link, not a button: SCO-NAV-03 B has hover swap the drawer and click follow
                the category URL. Focus activates too, so keyboards reach the same panels. */}
            <Link
                to={category.url}
                data-subnav-drawer-entry=""
                onMouseEnter={onActivate}
                onFocus={onActivate}
                onClick={onNavigate}
                aria-current={isActive ? 'true' : undefined}
                className={cn(
                    'relative flex h-14 items-center justify-between pr-8 pl-16 font-serif text-h4 leading-heading-tight font-normal text-primary',
                    // The highlight bleeds with the rail, or it stops short of the viewport
                    // edge above 1440 and notches the column.
                    isActive &&
                        'bg-secondary before:absolute before:inset-y-0 before:right-full before:w-screen before:bg-secondary'
                )}>
                {category.name}
                <span className="flex size-8 items-center justify-center">
                    <ChevronRight className="size-5" aria-hidden />
                </span>
            </Link>
        </li>
    );
}

/**
 * The featured subnav (SCO-NAV-03): featured links and a level 2 drawer on the left, the active
 * drawer's level 3/4 groups in the middle, the content component on the right. The tile is built
 * here, not handed in: SCO-NAV-03 D ties it to the level 2, and the open drawer is local state.
 */
export function FeaturedSubnav({
    category,
    renderContentSlot,
    onNavigate,
    initialDrawerId,
}: {
    category: NavTopCategory;
    renderContentSlot?: (category: NavCategory) => ReactNode;
    onNavigate?: () => void;
    /** The drawer open on first render; the first drawer when unset (the Builder preview sets it). */
    initialDrawerId?: string;
}): ReactElement {
    const drawers = category.children;
    const [activeId, setActiveId] = useState<string | undefined>(initialDrawerId ?? drawers[0]?.id);
    const active = drawers.find((drawer) => drawer.id === activeId) ?? drawers[0];
    const contentSlot = active ? renderContentSlot?.(active) : undefined;

    return (
        // The scroll container (SCO-483): max-height and overflow-y live here rather than on
        // the Radix Viewport, so the bar never lands on the node Radix measures. The clip
        // below keeps its own overflow-y unset, so it stays `clip` and the bleed is untouched.
        <div
            className="overflow-x-hidden overflow-y-auto bg-secondary"
            style={{ maxHeight: 'calc(100dvh - var(--header-height))' }}>
            {/* Clips the rail's bleed pseudo-element, which overshoots the left viewport edge. */}
            <div className="overflow-x-clip bg-secondary">
                <div className="mx-auto flex max-w-page items-start gap-8 pr-16">
                    {/* self-stretch, not content height: in Figma the white column runs the full
                        panel height whichever side is taller. ::before carries that white across
                        the max-w-page gutter, so above 1440 it still meets the viewport edge as
                        the beige does on the right. */}
                    <div className="relative flex w-[386px] shrink-0 flex-col gap-10 self-stretch bg-primary-foreground py-10 before:absolute before:inset-y-0 before:right-full before:w-screen before:bg-primary-foreground">
                        {category.featuredLinks.length > 0 ? (
                            <ul className="flex flex-col gap-3 pl-16">
                                {category.featuredLinks.map((link) => (
                                    <li key={link.url}>
                                        <Link
                                            to={link.url}
                                            onClick={onNavigate}
                                            className="text-body-s text-foreground">
                                            {link.text}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        ) : null}

                        <ul className="flex flex-col">
                            {drawers.map((drawer) => (
                                <DrawerEntry
                                    key={drawer.id}
                                    category={drawer}
                                    isActive={drawer.id === active?.id}
                                    onActivate={() => setActiveId(drawer.id)}
                                    onNavigate={onNavigate}
                                />
                            ))}
                        </ul>
                    </div>

                    <SubnavGroups
                        groups={active?.children ?? []}
                        columns={availableColumns(Boolean(contentSlot))}
                        className="min-w-px flex-1 py-10"
                        onNavigate={onNavigate}
                    />

                    {contentSlot ? <div className="flex shrink-0 items-center py-10">{contentSlot}</div> : null}
                </div>
            </div>
        </div>
    );
}
